import { useState, useEffect } from "react";
import { Plus, Clock, Trophy, Share2, BarChart2, HelpCircle, LogOut, Sun, Moon } from "lucide-react";
import { useTranslation } from "react-i18next";
import WebApp from "@twa-dev/sdk";
import { RecentSession } from "../../types/game";
import LanguageSwitcher from "../common/LanguageSwitcher";
import FontScaler from "../common/FontScaler";
import { formatGameDate } from "../../utils/date";
import RulesModal from "../common/RulesModal";
import { useTheme } from "../ThemeProvider";

const API = import.meta.env.VITE_API_URL || "/api/v1";
const BOT_USERNAME = import.meta.env.VITE_BOT_USERNAME || "";

interface Props {
  token: string;
  onCreateGame: () => void;
  onStartLocal: () => void;
  onJoinGame: (code: string) => void;
  onJoinTournament: (code: string) => void;
  onCreateTournament: () => void;
  onViewMatch?: (id: string) => void;
  onWatchGame?: (id: string) => void;
  onShowStats: () => void;
  onLogout: () => void;
}

function SkeletonCard() {
  return (
    <div className="w-full flex items-center justify-between p-3 glass rounded-2xl animate-pulse">
      <div className="flex flex-col gap-1.5">
        <div className="h-3 w-16 rounded" style={{ background: "rgba(255,255,255,0.1)" }} />
        <div className="h-2.5 w-32 rounded" style={{ background: "rgba(255,255,255,0.06)" }} />
      </div>
      <div className="h-4 w-10 rounded" style={{ background: "rgba(255,255,255,0.1)" }} />
    </div>
  );
}

export default function LobbyView({
  token,
  onCreateGame,
  onStartLocal,
  onJoinGame,
  onCreateTournament,
  onViewMatch,
  onWatchGame,
  onShowStats,
  onLogout,
}: Props) {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [code, setCode] = useState("");
  const [recent, setRecent] = useState<RecentSession[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [showRules, setShowRules] = useState(false);

  useEffect(() => {
    setLoadingRecent(true);
    fetch(`${API}/users/me/sessions`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { setRecent(data); setLoadingRecent(false); })
      .catch(() => setLoadingRecent(false));
  }, [token]);

  const handleJoin = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim()) {
      // Try both for now or we can implement a logic to detect
      onJoinGame(code.trim());
    }
  };

  const handleShareInvite = (inviteCode: string) => {
    const deepLink = BOT_USERNAME
      ? `https://t.me/${BOT_USERNAME}?startapp=${inviteCode}`
      : inviteCode;
    const msg = t("setup.inviteMessage", { code: inviteCode });
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(deepLink)}&text=${encodeURIComponent(msg)}`;
    try {
      WebApp.openTelegramLink(shareUrl);
    } catch {
      navigator.clipboard?.writeText(deepLink);
    }
  };

  return (
    <div className="min-h-screen text-ink flex flex-col p-4 pb-8">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
        {/* Top bar */}
        <div className="flex justify-between pt-3 pb-1">
          <button
            onClick={onLogout}
            className="p-2 text-ink/40 hover:text-red-400 transition-colors"
            title="Log out"
          >
            <LogOut size={18} />
          </button>
          <div className="flex gap-1">
            <button
              onClick={toggleTheme}
              className="p-2 text-ink/40 hover:text-ink/70 transition-colors"
              title={theme === "dark" ? "Light theme" : "Dark theme"}
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <FontScaler />
            <LanguageSwitcher />
          </div>
        </div>

        {/* Header */}
        <div className="text-center py-6">
          <div className="text-6xl mb-3 select-none drop-shadow-2xl">🎲</div>
          <h1 className="text-3xl font-bold tracking-tight">{t("app.title")}</h1>
          <p className="text-ink/40 mt-1 text-sm">{t("app.tagline")}</p>
        </div>

        {/* New Game + New Tournament */}
        <div className="flex flex-col gap-2 mb-3">
          <button
            onClick={onCreateGame}
            className="flex items-center justify-center gap-2 py-4 rounded-2xl font-semibold text-lg transition-all active:scale-95 select-none"
            style={{
              background: "linear-gradient(135deg, rgba(59,130,246,0.85), rgba(99,102,241,0.85))",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(99,130,246,0.4)",
              boxShadow: "0 8px 32px rgba(59,130,246,0.35), inset 0 1px 0 rgba(255,255,255,0.2)",
            }}
          >
            <Plus size={20} />
            {t("lobby.newGame")}
          </button>
          <button
            onClick={onCreateTournament}
            className="flex items-center justify-center gap-2 py-4 rounded-2xl font-semibold text-lg transition-all active:scale-95 select-none"
            style={{
              background: "linear-gradient(135deg, rgba(202,138,4,0.85), rgba(234,179,8,0.75))",
              backdropFilter: "blur(12px)",
              border: "1px solid rgba(202,138,4,0.45)",
              boxShadow: "0 8px 32px rgba(202,138,4,0.35), inset 0 1px 0 rgba(255,255,255,0.2)",
            }}
          >
            <Trophy size={20} />
            {t("tournament.newTournament")}
          </button>
        </div>

        {/* Join by code */}
        <form onSubmit={handleJoin} className="flex gap-2 mb-3">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={6}
            placeholder={t("lobby.codePlaceholder")}
            className="glass-input flex-1 min-w-0 px-4 py-3 rounded-xl text-center tracking-widest font-mono text-lg uppercase"
          />
          <button
            type="submit"
            disabled={code.trim().length < 4}
            className="glass-btn shrink-0 px-5 py-3 rounded-xl disabled:opacity-40 text-ink/80 font-semibold transition-all"
          >
            {t("lobby.enter")}
          </button>
        </form>

        {/* Rating + Rules row */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={onShowStats}
            className="glass flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-ink/60 hover:text-ink transition-colors"
          >
            <BarChart2 size={16} />
            <span className="text-sm font-semibold">{t("lobby.rating")}</span>
          </button>
          <button
            onClick={() => setShowRules(true)}
            className="glass flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-ink/60 hover:text-ink transition-colors"
          >
            <HelpCircle size={16} />
            <span className="text-sm font-semibold">{t("lobby.rules")}</span>
          </button>
        </div>

        <button
          onClick={onStartLocal}
          className="glass w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-semibold text-lg transition-all active:scale-95 select-none mb-8 text-ink/70 hover:text-ink"
        >
          <Clock size={20} className="text-ink/40" />
          {t("lobby.offlineGame")}
        </button>

        {/* Recent games */}
        <div>
          <h2 className="text-[10px] font-black text-ink/30 uppercase tracking-[0.22em] mb-3 flex items-center gap-1.5">
            <Clock size={11} /> {t("lobby.recentGames")}
          </h2>
          <div className="space-y-2">
            {loadingRecent ? (
              <>
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : recent.length === 0 ? (
              <p className="text-center text-ink/25 text-sm py-4">
                {t("lobby.noGames", "No recent games")}
              </p>
            ) : (
              recent.map((s) => {
                const isTournament = s.type === "tournament";
                const you = s.players.find((p) => p.is_you);
                const top = [...s.players].sort((a, b) => b.grand_total - a.grand_total)[0];
                return (
                  <div
                    key={s.id}
                    className="glass w-full flex items-center justify-between p-3 rounded-2xl transition-all"
                    style={isTournament ? { borderColor: "rgba(202,138,4,0.3)" } : {}}
                  >
                    <button
                      className="flex-1 text-left"
                      onClick={() => {
                        if (isTournament) {
                          onJoinGame(s.invite_code);
                        } else if (s.status === "finished" && onViewMatch) {
                          onViewMatch(s.id);
                        } else {
                          onJoinGame(s.invite_code);
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold ${isTournament ? "text-yellow-400" : "text-blue-400"}`}>
                          {isTournament ? s.name : formatGameDate(s.created_at, i18n.language)}
                        </span>
                        {isTournament && (
                          <span className="text-[10px] bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded-full font-black uppercase tracking-tighter border border-yellow-500/30">
                            {s.current_game}/{s.total_games}
                          </span>
                        )}
                        {s.status === "finished" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full text-ink/30 border border-ink/10">
                            {t("lobby.finished")}
                          </span>
                        )}
                        {s.status === "active" && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full animate-pulse font-bold border ${
                            isTournament
                              ? "bg-yellow-500/15 text-yellow-400 border-yellow-500/25"
                              : "bg-green-500/15 text-green-400 border-green-500/25"
                          }`}>
                            {t("lobby.live")}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-ink/35 mt-0.5">
                        {isTournament && <span className="text-[10px] text-ink/25 mr-1 italic">{formatGameDate(s.created_at, i18n.language)} •</span>}
                        {s.players.map((p) => p.display_name).join(", ")}
                      </div>
                    </button>

                    <div className="flex items-center gap-2 ml-2 shrink-0">
                      {you && (
                        <div className="text-sm font-bold text-ink text-right">
                          {you.grand_total}
                          {top?.is_you && s.status === "finished" && (
                            <div className="text-[10px] text-yellow-400 flex items-center gap-0.5 justify-end">
                              <Trophy size={9} /> {t("lobby.won")}
                            </div>
                          )}
                        </div>
                      )}
                      {!you && s.status === "active" && !isTournament && onWatchGame && (
                        <button
                          onClick={() => onWatchGame(s.id)}
                          className="text-[10px] font-bold text-purple-400 bg-purple-500/10 border border-purple-500/20 px-2 py-1 rounded-lg hover:bg-purple-500/20 transition-colors"
                          title={t("tournament.spectatorMode")}
                        >
                          👁 Watch
                        </button>
                      )}
                      {s.status !== "finished" && (
                        <button
                          onClick={() => handleShareInvite(s.invite_code)}
                          className="p-1.5 text-ink/25 hover:text-blue-400 transition-colors"
                          title={t("setup.shareInvite")}
                        >
                          <Share2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      {showRules && <RulesModal onClose={() => setShowRules(false)} />}
    </div>
  );
}
